/**
 * The kitchen display — the pass.
 *
 * Read from one to two metres, through steam, by someone with both hands busy.
 * Every choice follows from that:
 *
 * - Dockets are cards on a rail, oldest first. A kitchen works left to right.
 * - The clock is the largest thing on a docket. It is the only number that
 *   changes and the only one anyone reads at distance.
 * - Colour is time against the station's target and nothing else — and it is
 *   never the only carrier. Border weight steps 2px → 4px → 6px with urgency,
 *   and the docket says its state in words to a screen reader. Steam, glare
 *   and red-green colour blindness all take the hue away; none of them take
 *   the thickness of a rule away.
 * - Bumping is one large tap with no confirmation. A cook holding a pan does
 *   not get a second gesture. It is outlined rather than filled because this
 *   screen is on for sixteen hours and a big block of solid colour in a fixed
 *   position is how a panel gets burnt.
 */

import { useState } from 'react';
import {
  formatElapsed,
  setDisplayFormat,
  urgencyAt,
  useBumpTicket,
  useEntitlements,
  useKitchenPace,
  useLocations,
  usePermission,
  usePassRail,
  useRail,
  useRailStream,
  useRecallTicket,
  useSession,
  useStations,
  useTicking,
  type Ticket,
} from '@restaurant/api-client';
import { Button, Empty, ErrorNotice, Loading, Sheet, UpgradePrompt } from '@restaurant/ui';

import { PassDocketCard } from './pass';
import { PrinterView } from './printer';
import { TabNote } from './tab-note';
import './styles.css';

const minutes = (seconds: number) => `${Math.round(seconds / 60)} min`;

const URGENCY_WORD: Record<string, string> = {
  on_time: 'on time',
  approaching: 'nearly due',
  late: 'over target',
};

export function App() {
  const session = useSession();
  const entitlements = useEntitlements();
  const locations = useLocations();
  // Remembered on the device: a screen is bolted above one station, and
  // coming back on the first station after every reload was a tap each time.
  const [stationId, setStationIdState] = useState<string | null>(() => readStation());
  const setStationId = (id: string) => {
    setStationIdState(id);
    writeStation(id);
  };
  const [choosing, setChoosing] = useState(false);
  const mayChangePlan = usePermission('billing.subscription.change');

  const locationId = locations.data?.[0]?.id;
  const site = locations.data?.[0];
  if (site) setDisplayFormat({ currency: site.currency, locale: site.locale });
  const stations = useStations(locationId);
  const active = stations.data?.find((s) => s.id === stationId) ?? stations.data?.[0] ?? null;
  // A screen is a prep rail or the pass, and they ask different questions of
  // the same kitchen. Only one of the two is ever fetched: the other's query
  // is disabled by a null id rather than fetched and discarded, because this
  // screen is on a tablet at the end of a service and every poll it does not
  // make is one it does not have to.
  const isPass = active?.kind === 'expo';
  // A printer reads its own queue rather than the rail, but keeps the socket:
  // the nudge that tells a screen a ticket landed is what prints it promptly.
  const isPrinter = active?.kind === 'printer';
  const rail = useRail(isPass || isPrinter ? null : (active?.id ?? null));
  const pass = usePassRail(isPass ? (active?.id ?? null) : null);
  const streamStatus = useRailStream(isPass ? null : (active?.id ?? null));

  if (locations.isPending || stations.isPending) {
    return <Loading label="Connecting to the pass" />;
  }
  if (locations.isError) return <ErrorNotice message={locations.error.message} />;
  if (stations.isError) return <ErrorNotice message={stations.error.message} />;

  if (!active) {
    return (
      <div className="kds">
        <header className="kds__bar">
          <h1 className="kds__station">Kitchen</h1>
          <Button variant="ghost" onClick={session.signOut}>
            Sign out
          </Button>
        </header>
        <main className="kds__rail kds__rail--empty">
          <Empty
            title="No screen set up yet"
            description="Add a station in the back office and dockets appear here as orders are taken."
          />
        </main>
      </div>
    );
  }

  const tickets = rail.data ?? [];
  const dockets = pass.data ?? [];
  const empty = isPass ? dockets.length === 0 : tickets.length === 0;
  const failure = isPass ? pass.error : rail.error;

  return (
    <div className="kds">
      <header className="kds__bar">
        <div className="kds__ident">
          {/* Which station, and everything that is not cooking — another
              station, signing out — behind it. Station tabs across the top
              were cut off past three, and a "Sign out" always on screen is one
              elbow away from emptying the rail mid-service. */}
          <button type="button" className="kds__station-btn" onClick={() => setChoosing(true)}>
            <h1 className="kds__station">{active.name}</h1>
            <span className="kds__station-more" aria-hidden="true">
              ▾
            </span>
          </button>
          {active.sla_seconds !== null && !isPrinter && (
            <span className="kds__target">{minutes(active.sla_seconds)} target</span>
          )}
          {!isPass && !isPrinter && locationId && (
            <TodaySoFar locationId={locationId} stationId={active.id} />
          )}
        </div>

        <div className="kds__bar-right">
          {/* A screen that has quietly stopped listening looks exactly like a
              quiet kitchen. Say which one it is, in a word, always.

              The pass has no socket — it is assembled from several stations'
              dockets rather than pushed — so it says how it keeps up instead
              of claiming a connection it does not have. */}
          {isPass ? (
            <span className="kds__link kds__link--live">
              <span className="kds__link-dot" aria-hidden="true" />
              Every 5s
            </span>
          ) : (
            <span className={`kds__link kds__link--${streamStatus}`}>
              <span className="kds__link-dot" aria-hidden="true" />
              {streamStatus === 'live'
                ? 'Live'
                : streamStatus === 'connecting'
                  ? 'Connecting'
                  : 'Offline'}
            </span>
          )}
        </div>
      </header>

      {choosing && (
        <Sheet
          title="This screen"
          onDismiss={() => setChoosing(false)}
          actions={
            <Button variant="secondary" size="lg" block onClick={session.signOut}>
              Sign out
            </Button>
          }
        >
          {(stations.data ?? []).length > 1 && (
            <ul className="kds__stations">
              {(stations.data ?? []).map((station) => (
                <li key={station.id}>
                  <button
                    type="button"
                    className={`kds__station-pick${station.id === active.id ? ' is-on' : ''}`}
                    aria-pressed={station.id === active.id}
                    onClick={() => {
                      setStationId(station.id);
                      setChoosing(false);
                    }}
                  >
                    <span>{station.name}</span>
                    {station.open_tickets > 0 && (
                      <span className="kds__station-count tabular">{station.open_tickets}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Sheet>
      )}

      <main className={`kds__rail${empty && !isPrinter ? ' kds__rail--empty' : ''}`}>
        {failure && <ErrorNotice message={failure.message} />}
        {isPrinter ? (
          <PrinterView station={active} />
        ) : empty && !failure ? (
          <Empty
            title="Nothing on"
            description={
              isPass
                ? 'Courses appear here as the kitchen starts them, and stay until they are sent.'
                : 'Dockets appear the moment an order is fired.'
            }
          />
        ) : isPass ? (
          dockets.map((docket) => (
            <PassDocketCard
              key={`${docket.order_id}-${docket.course_no}`}
              docket={docket}
              stationId={active.id}
            />
          ))
        ) : (
          tickets.map((ticket) => <Docket key={ticket.id} ticket={ticket} />)
        )}
      </main>

      <footer className="kds__foot">
        {/* The legend names the second channel as well as the colour, because
            the second channel is the one that survives a steamed-up screen. */}
        {/* No legend without a target. A key explaining colours the rail is
            not using is worse than no key: it tells a cook to read something
            that is not there. */}
        {/* Offers to upgrade are for whoever pays, not for the cook. */}
        {isPrinter ? null : active.sla_seconds !== null ? (
          <ul className="kds__legend">
            <li className="kds__key kds__key--on_time">
              <span className="kds__key-rule" aria-hidden="true" />
              On time
            </li>
            <li className="kds__key kds__key--approaching">
              <span className="kds__key-rule" aria-hidden="true" />
              Nearly {minutes(active.sla_seconds)}
            </li>
            <li className="kds__key kds__key--late">
              <span className="kds__key-rule" aria-hidden="true" />
              Over
            </li>
          </ul>
        ) : !mayChangePlan ? null : (
          <UpgradePrompt
            action="Prep-time targets and colour escalation"
            currentPlan={entitlements.plan}
            requiredPlan="professional"
            compact
          />
        )}

        {mayChangePlan && !entitlements.has('kds.multi_station') && (
          <UpgradePrompt
            action="Multiple stations and routing"
            cap={1}
            currentPlan={entitlements.plan}
            requiredPlan="professional"
            compact
          />
        )}
      </footer>
    </div>
  );
}

/**
 * This station's service so far, small, in the corner: tickets out, the
 * typical time, how many went over. Enough for a head chef to glance at
 * mid-service; the breakdown lives in the back office. Nothing at all without
 * `kds.analytics` or the permission to read it — a cook does not need an
 * upgrade prompt on the wall.
 */
function TodaySoFar({ locationId, stationId }: { locationId: string; stationId: string }) {
  const entitlements = useEntitlements();
  const mayRead = usePermission('kds.analytics.read');
  const shown = entitlements.has('kds.analytics') && mayRead;
  const pace = useKitchenPace({ locationId, stationId }, shown);
  if (!shown || !pace.data || pace.data.pace.tickets === 0) return null;
  const { tickets, median_seconds, late } = pace.data.pace;
  return (
    <span className="kds__target kds__today">
      Today {tickets} out
      {median_seconds !== null && ` · typically ${formatElapsed(median_seconds)}`}
      {late > 0 && ` · ${late} over`}
    </span>
  );
}

function Docket({ ticket }: { ticket: Ticket }) {
  const bump = useBumpTicket();
  const recall = useRecallTicket();
  const elapsed = useTicking(ticket.elapsed_seconds);
  // Null urgency means the plan has no prep targets: the docket keeps its
  // clock and its bump bar, and carries no colour at all.
  const urgency = ticket.fired_at ? urgencyAt(elapsed, ticket.sla_seconds) : 'on_time';
  // Named as the till names it, so "Table 12" on the till is "Table 12" here.
  const table = ticket.table_label
    ? `Table ${ticket.table_label}`
    : `Order ${ticket.order_number}`;
  // Read out with the site it is for, when it is cooked here for another one.
  const where = ticket.from_location_name ? `${table} for ${ticket.from_location_name}` : table;

  return (
    <article
      // No urgency modifier when the plan has no target: the base docket
      // style is already the neutral one — there is no `.dk--on_time` rule,
      // on time *is* the 2px line — so a docket with no colour to carry
      // renders correctly by carrying no modifier.
      className={`dk${urgency ? ` dk--${urgency}` : ''}${ticket.fired_at ? '' : ' dk--held'}`}
      aria-label={
        ticket.fired_at
          ? urgency
            ? `${where}, ${formatElapsed(elapsed)}, ${URGENCY_WORD[urgency] ?? urgency}`
            : `${where}, ${formatElapsed(elapsed)}`
          : `${where}, held`
      }
    >
      <header className="dk__head">
        <span className="dk__where">{table}</span>
        {ticket.course_no > 1 && <span className="dk__course">Course {ticket.course_no}</span>}
        {/* Dishes added after this station bumped the course. Said, so a lone
            side is not plated as if it were the whole course. */}
        {ticket.follow_up && <span className="dk__added">Added</span>}
        {/* Cooked here for another site. Said on the docket, because table 12
            across town is not table 12 in this room. */}
        {ticket.from_location_name && (
          <span className="dk__for">For {ticket.from_location_name}</span>
        )}
        {/* The largest thing on the docket. It is what everyone reads first. */}
        <span className="dk__clock">{ticket.fired_at ? formatElapsed(elapsed) : 'held'}</span>
      </header>

      <TabNote note={ticket.order_note} />

      <ul className="dk__items">
        {sameDishesTogether(ticket.items).map((item) => (
          <li key={item.id} className="dk__item">
            <span className="dk__qty">{item.count}</span>
            <span className="dk__text">
              <span className="dk__name">{item.name}</span>
              {item.modifiers.length > 0 && (
                <span className="dk__mods">{item.modifiers.join(' · ')}</span>
              )}
              {item.note && <span className="dk__note">{item.note}</span>}
              {item.seat_no !== null && <span className="dk__seat">Seat {item.seat_no}</span>}
            </span>
          </li>
        ))}
      </ul>

      <footer className="dk__foot">
        {ticket.state === 'bumped' ? (
          <button
            type="button"
            className="dk__act dk__act--recall"
            onClick={() => recall.mutate({ ticketId: ticket.id })}
          >
            Recall
          </button>
        ) : (
          <button
            type="button"
            className="dk__act"
            onClick={() => bump.mutate({ ticketId: ticket.id })}
            aria-label={`Bump ${where}`}
          >
            Bump
          </button>
        )}
      </footer>
    </article>
  );
}

const STATION_KEY = 'kds.station';

function readStation(): string | null {
  try {
    return localStorage.getItem(STATION_KEY);
  } catch {
    return null;
  }
}

function writeStation(id: string): void {
  try {
    localStorage.setItem(STATION_KEY, id);
  } catch {
    // Storage refused: the choice holds until the page reloads.
  }
}

/**
 * The same dish rung in twice, as one line with its count: "2 Salt cod
 * croquettes" rather than the dish twice, which a cook reads as two different
 * things. Only when everything about them matches — choices, note and seat —
 * because two of a dish cooked differently are two lines.
 */
function sameDishesTogether(
  items: Ticket['items'],
): (Ticket['items'][number] & { count: number })[] {
  const out: (Ticket['items'][number] & { count: number })[] = [];
  for (const item of items) {
    const twin = out.find(
      (seen) =>
        seen.name === item.name &&
        seen.note === item.note &&
        seen.seat_no === item.seat_no &&
        seen.modifiers.join('|') === item.modifiers.join('|'),
    );
    if (twin) twin.count += Number(item.qty);
    else out.push({ ...item, count: Number(item.qty) });
  }
  return out;
}
